import { generateUniqueId } from "components/utils";
import { shuffle } from "components/utils/shuffle";
import { DashboardContext } from "contexts/DashboardContext";
import { useContext, useState } from "react";
import { Button, Form } from "react-bootstrap";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";

import GroupSettingsModal from "./GroupModal";
import { saveGroupConfigurationsGloballyGenric } from "./Helper";
import { SavedGroupConfigs } from "./savedGroupConfig";
import { Group, SavedGroupConfigurations } from "./types";

interface Props {}

export const OrganiseGroupSetting: WidgetSettingFC<SavedGroupConfigurations[], Props> = (props) => {
  const { data, setData, settings, updateAnyWidgetSetting, info, state } = props;
  const [isSaveChangedButtonDisabled, setSaveChangesAddButtonDisabled] = useState(false);
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);
  const [isToggled, setIsToggled] = useState(false); // Toggle state
  //for Global Data
  const {
    data: { savedGroupConfigurations = [] },
    setData: setGlobalData,
  } = useContext(DashboardContext);

  const saveGroupConfigurationsGlobally = (newConfigurations: SavedGroupConfigurations[]) => {
    setGlobalData({ savedGroupConfigurations: newConfigurations });
    // setData(newConfigurations);
  };

  //for group config data state
  const [groupConfig, setGroupConfig] = useState<SavedGroupConfigurations>({
    id: 0,
    groupConfigName: "Default Group Configuration",
    groups: [],
    groupCount: 1,
    groupingMode: "Auto",
    showPoints: true,
  });

  //for active selection index
  const [selectedGroupConfigIndex, setSelectedGroupConfigIndex] = useState<number | undefined>();

  //for pop up modal to show groups
  const [showGroupModal, setShowGroupModal] = useState(false);
  const handleCloseModal = () => {
    setSelectedGroupConfigIndex(undefined);
    setGroupConfig({
      id: generateUniqueId(),
      groupConfigName: "Default Group Configuration",
      groups: [],
      groupCount: 1,
      groupingMode: "Auto",
    });

    // Close the modal
    setShowGroupModal(false);
    setSaveChangesAddButtonDisabled(false);
    setIsAddButtonDisabled(false);
  };

  const cleanNameList: string[] = settings?.nameList?.split("\n").filter(Boolean) || [];
  const randomiseGroups = () => {
    setSelectedGroupConfigIndex(undefined);
    const numberOfGroups = settings.groupCount || 1;
    const shuffledNames = shuffle(cleanNameList);
    const newGroups: Group[] = Array.from({ length: numberOfGroups }, (_, i) => ({
      title: `Group ${i + 1}`,
      students: [],
      point: 0,
    }));

    shuffledNames.forEach((name, index) => {
      newGroups[index % numberOfGroups].students.push(name);
    });

    setGroupConfig({
      id: generateUniqueId(),
      groupConfigName: "Auto Configuration",
      groups: newGroups,
      groupCount: numberOfGroups,
      groupingMode: "Auto",
      showPoints: true,
    });
    if (groupConfig?.groups?.length) {
      setSaveChangesAddButtonDisabled(false);
    }
  };
  const mannualGroupFormation = () => {
    setGroupConfig({
      id: generateUniqueId(),
      groupConfigName: "Manual Configuration",
      groups: Array.from({ length: 1 }, (_, i) => ({
        title: `Group ${i + 1}`,
        students: [],
        point: 0,
      })),
      groupCount: 1,
      groupingMode: "Manual",
      showPoints: true,
    });
    if (groupConfig?.groups?.length) {
      setSaveChangesAddButtonDisabled(false);
    }
  };

  const handleButtonClick = () => {
    setShowGroupModal(true);

    if ((settings?.groupingMode ?? settings?.organise?.groupingMode) === "Auto") {
      randomiseGroups();
    } else if ((settings?.groupingMode ?? settings?.organise?.groupingMode) === "Manual") {
      mannualGroupFormation();
    }
  };
  const handlePointsToggle = () => {
    const toggled = !isToggled;
    setIsToggled(toggled);

    // Update showPoints only for the groupConfigIndex object
    const updatedData = data.map((item) => {
      if (item.id === data[0]?.id) {
        // Update only the matching object
        return { ...item, showPoints: toggled };
      }
      return item; // Leave other objects unchanged
    });

    // Update the local state
    setData(updatedData);

    // Find the updated configuration
    const updatedConfig = updatedData.find((item) => item.id === data[0]?.id);

    // Check if updatedConfig is valid before calling the function
    if (updatedConfig) {
      saveGroupConfigurationsGloballyGenric(
        updatedConfig, // Pass the updated configuration
        savedGroupConfigurations, // Existing saved configurations
        setGlobalData, // Function to update global data
      );
    } else {
      console.error(`Configuration with id ${data[0]?.id} not found.`);
    }
  };
  return (
    <OuterContainer>
      <Button
        style={{ color: "white", background: "#8f84de", marginBottom: "1rem" }}
        onClick={handleButtonClick}
        disabled={settings?.groupCount > 20 || settings?.groupCount < 1 || !settings?.groupCount}
      >
        {`${
          (settings?.groupingMode ?? settings?.organise?.groupingMode) === "Auto"
            ? "Generate New Group"
            : "Create New Group"
        }`}
      </Button>
      {savedGroupConfigurations?.length > 0 && (
        <InnerContainer>
          <label
            style={{
              fontWeight: "600",
              fontSize: "12pt",
              color: "#363636",
            }}
          >
            {"Show Points"}
          </label>
          <Switch>
            <Form.Control
              checked={data?.[0]?.showPoints}
              type="checkbox"
              onChange={handlePointsToggle}
            />

            <Slider />
          </Switch>
        </InnerContainer>
      )}
      <SavedGroupConfigs
        savedGroupConfigurationsData={savedGroupConfigurations}
        onSubmit={saveGroupConfigurationsGlobally}
        setShowGroupModal={setShowGroupModal}
        selectedGroupConfigIndex={selectedGroupConfigIndex}
        setSelectedGroupConfigIndex={setSelectedGroupConfigIndex}
        currentStateGroupConfigData={groupConfig}
        setGroupConfig={setGroupConfig}
        setData={setData}
        data={data}
        settings={settings}
      />
      <GroupSettingsModal
        show={showGroupModal}
        onClose={handleCloseModal}
        settings={settings}
        savedGroupConfigurations={savedGroupConfigurations}
        currentStateGroupConfigData={groupConfig}
        setGroupConfig={setGroupConfig}
        saveGroupConfigurationsGlobally={saveGroupConfigurationsGlobally}
        selectedGroupConfigIndex={selectedGroupConfigIndex}
        setSelectedGroupConfigIndex={setSelectedGroupConfigIndex}
        setData={setData}
        setSaveChangesAddButtonDisabled={setSaveChangesAddButtonDisabled}
        isSaveChangedButtonDisabled={isSaveChangedButtonDisabled}
        setIsAddButtonDisabled={setIsAddButtonDisabled}
        isAddButtonDisabled={isAddButtonDisabled}
        data={data}
      />
    </OuterContainer>
  );
};

const OuterContainer = styled.div`
  width: 100%;
  margin-bottom: 35px;
`;

const Row = styled.div<{ center?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props) => (props.center ? `space-evenly` : "flex-start")};
  gap: 8px;
`;

const InnerContainer = styled(Row)`
  align-items: center;
  gap: 10px;
  margin-bottom: 30px;
  margin-top: 30px;
  span {
    color: var(--gray);
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 26px;

  :before {
    position: absolute;
    content: "";
    height: 80%;
    aspect-ratio: 1;
    left: 0;
    top: 50%;
    transform: translate(15%, -50%);
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const Switch = styled(Form.Label)`
  position: relative;
  display: inline-block;
  height: 30px;
  margin: 0;
  aspect-ratio: 2;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  input:checked + ${Slider} {
    background-color: var(--primary);
  }

  input:checked + ${Slider}:before {
    left: 100%;
    transform: translate(-115%, -50%);
  }
`;
